import { useEffect, useRef } from "react";
import ChatBubble from "./ChatBubble";

export interface BubbleEntry {
  id: string;
  who: "elena" | "student";
  text: string;
}

interface Props {
  entries: BubbleEntry[];
  thinking?: boolean;      // Elena composing her reply (left side)
  transcribing?: boolean;  // student's speech being transcribed (right side)
}

function Dots() {
  return (
    <div className="flex gap-[5px] items-center h-5">
      <span className="thinking-dot" style={{ animationDelay: "0ms" }} />
      <span className="thinking-dot" style={{ animationDelay: "160ms" }} />
      <span className="thinking-dot" style={{ animationDelay: "320ms" }} />
    </div>
  );
}

export default function Transcript({ entries, thinking, transcribing }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };

  useEffect(() => {
    if (nearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [entries, thinking, transcribing]);

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
      <div className="max-w-2xl mx-auto w-full px-5 sm:px-8 py-8 flex flex-col gap-6 min-h-full">
        {entries.length === 0 && !thinking && !transcribing && (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-text-tertiary italic text-sm text-center leading-relaxed">
              Say ciao — tap the mic and start talking.
              <br />
              Your conversation will appear here.
            </p>
          </div>
        )}
        {entries.map((e, i) => (
          <ChatBubble
            key={e.id}
            who={e.who}
            text={e.text}
            thinking={thinking && i === entries.length - 1 && e.who === "elena"}
          />
        ))}
        {transcribing && (
          <div className="self-end max-w-[80%] animate-fade-in flex flex-col items-end" role="status" aria-label="Transcribing your speech">
            <div className="font-display font-semibold tracking-[-0.01em] text-text-secondary text-[0.8125rem] mb-1">You</div>
            <div className="bg-[#26262b] rounded-2xl rounded-br-md px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              <Dots />
            </div>
          </div>
        )}
        {thinking && entries[entries.length - 1]?.who !== "elena" && (
          <div className="self-start max-w-[88%] animate-fade-in" role="status" aria-label="Elena is composing her reply">
            <div className="font-display font-semibold tracking-[-0.01em] text-success text-[0.8125rem] mb-1">Elena</div>
            <div className="bg-surface2 rounded-2xl rounded-bl-md px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              <Dots />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
